// Import `globalCache` from the direct file path rather than the
// `@sbrc/utils` barrel, which transitively loads window.location-reading
// helpers (apiEndpoint, ...) that node test environments cannot load
// cleanly. globalCache is dependency-free so a direct import is lighter
// and honest about what we depend on.
import { globalCache } from '@sbrc/utils/globalCache';

import type { AuthTokenProvider } from './apiAuthTokenProvider';
import { createApiAuthTokenProvider } from './apiAuthTokenProvider';
import { startNDJSONStream } from './NDJSONStreamClient';

/** Payload posted to `/api/v1/agent/send`. */
export interface ChatSendBody {
  sessionID: string | null;
  /**
   * The routine the user has open in the Routine editor. URL-derived
   * on `/routines/:id` with no fallback to `robot.latestRoutineID` —
   * null off the editor page. Lets the agent resolve unqualified
   * references like "this routine" / "edit this" against the page
   * the operator is on.
   */
  editingRoutineID: string | null;
  /**
   * The routine loaded on the robot (`robot.latestRoutineID`) — the
   * one that runs on Play. Null when nothing is loaded. Lets the
   * agent resolve "the running routine" / "what's loaded" even when
   * the editor is showing a different routine.
   */
  loadedRoutineID: string | null;
  text: string;
}

/** Payload posted to `/api/v1/agent/cancel`. */
export interface ChatCancelBody {
  sessionID: string;
}

/** Payload posted to `/api/v1/agent/confirm` — matches the agent-bot
 * ConfirmBodySchema ({ sessionID, toolUseID, approved }). */
export interface ChatConfirmBody {
  sessionID: string;
  toolUseID: string;
  approved: boolean;
}

export interface ChatEventsOptions {
  sessionID: string;
  /** Fires once per parsed event in arrival order. */
  onEvent: (event: unknown) => void;
  /**
   * Fires on a network error, an HTTP non-2xx response, a missing
   * response body, or a malformed NDJSON line. Surfacing is
   * best-effort — the underlying stream keeps running (reconnects
   * or skips the bad line) so a single failure doesn't tear down
   * the chat.
   */
  onError?: (error: Error) => void;
  /** Aborting this signal stops the stream and any pending reconnect. */
  signal: AbortSignal;
}

/**
 * Boundary between the chat panel and whichever backend it talks
 * to. The mount calls into a `ChatTransport` for every wire-level
 * interaction; production uses {@link createRealChatTransport},
 * but tests and the UI-only dev harness can pass any implementation
 * that satisfies this contract.
 *
 * Keeping the wire format behind an interface (rather than fetch /
 * stream calls scattered through the mount) makes it easy to swap
 * the transport for mocks, change auth strategies, or route through
 * a different host on the control box without churning the mount.
 */
export interface ChatTransport {
  /** POST `/api/v1/agent/send`. */
  send(body: ChatSendBody): Promise<Response>;
  /** POST `/api/v1/agent/cancel`. */
  cancel(body: ChatCancelBody): Promise<Response>;
  /** POST `/api/v1/agent/confirm`. */
  confirm(body: ChatConfirmBody): Promise<Response>;
  /**
   * Open a long-lived NDJSON event stream against
   * `/api/v1/agent/sessions/{sessionID}/events`. Caller passes an AbortSignal to stop
   * the stream and any pending reconnect.
   */
  events(options: ChatEventsOptions): void;
}

const BASE_URL = '/api/v1/agent';
const SEND_URL = `${BASE_URL}/send`;
const CANCEL_URL = `${BASE_URL}/cancel`;
const CONFIRM_URL = `${BASE_URL}/confirm`;
const SESSIONS_URL = `${BASE_URL}/sessions`;

/**
 * Best-effort token resolution: when the mint is unavailable (feathers
 * session not yet established, service down) the request still goes
 * out unauthenticated and the server decides — same failure surface
 * the caller already handles via the Response status.
 */
async function resolveToken(
  tokenProvider: AuthTokenProvider,
): Promise<string | null> {
  try {
    return await tokenProvider.get();
  } catch {
    return null;
  }
}

async function postJSON(
  url: string,
  body: unknown,
  tokenProvider: AuthTokenProvider,
): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    const token = await resolveToken(tokenProvider);

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(body),
    });
  };

  let response = await attempt();

  // A 401 means the cached token expired or was evicted server-side;
  // re-mint once and retry. A second 401 is a real auth failure and is
  // returned to the caller.
  if (response.status === 401) {
    tokenProvider.invalidate();
    response = await attempt();
  }

  return response;
}

/**
 * Process-wide default token provider. Shared across every
 * `createRealChatTransport()` call — and therefore across chat remounts
 * (e.g. `RoutineAgentChatMount`, which builds a transport per mount) —
 * so a still-valid minted token is reused instead of re-minted on each
 * remount. `globalCache` also survives Hot Module Refresh. Callers that
 * inject their own provider bypass this entirely; only the default is
 * shared.
 */
function getDefaultApiAuthTokenProvider(): AuthTokenProvider {
  return globalCache('chatTransport.apiAuthTokenProvider', () =>
    createApiAuthTokenProvider(),
  );
}

/**
 * Default transport: hits the agent-bot HTTP endpoints directly.
 * Used in production and any environment with a real backend.
 */
export function createRealChatTransport(
  tokenProvider: AuthTokenProvider = getDefaultApiAuthTokenProvider(),
): ChatTransport {
  return {
    send: (body) => postJSON(SEND_URL, body, tokenProvider),
    cancel: (body) => postJSON(CANCEL_URL, body, tokenProvider),
    confirm: (body) => postJSON(CONFIRM_URL, body, tokenProvider),
    events: ({ sessionID, onEvent, onError, signal }) => {
      startNDJSONStream({
        url: `${SESSIONS_URL}/${encodeURIComponent(sessionID)}/events`,
        onEvent,
        onError,
        signal,
        getHeaders: async () => {
          const token = await resolveToken(tokenProvider);
          const headers: Record<string, string> = {};

          if (token !== null) {
            headers.Authorization = `Bearer ${token}`;
          }

          return headers;
        },
        onUnauthorized: () => tokenProvider.invalidate(),
      });
    },
  };
}
